import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

// Configuração global do axios para CORS
axios.defaults.withCredentials = false
axios.defaults.headers.common['Accept'] = 'application/json'

export interface ClickUpWorkspace {
  id: string
  name: string
  color: string
  avatar: string | null
  members: any[]
}

export interface ClickUpSpace {
  id: string
  name: string
  private: boolean
  statuses: any[]
  multiple_assignees: boolean
  features: any
}

export interface ClickUpFolder {
  id: string
  name: string
  hidden: boolean
  task_count: number
}

export interface ClickUpList {
  id: string
  name: string
  task_count: number
  status: any
}

export interface ClickUpTask {
  id: string
  name: string
  text_content: string
  description: string
  status: {
    status: string
    color: string
    type: string
    orderindex: number
  }
  priority: {
    priority: string
    color: string
  }
  due_date: string | null
  assignees: any[]
  time_estimate: number | null
  time_spent: number | null
  tags: any[]
  checklists: any[]
  attachments: any[]
}

export interface ClickUpIntegration {
  id: number
  instance_id: number
  user_id: number
  clickup_user_id: string
  clickup_username: string
  is_active: boolean
  last_sync_at: string | null
  sync_frequency: 'daily' | 'weekly' | 'manual'
  webhook_id: string | null
  webhook_url: string | null
  settings: any
  created_at: string
  updated_at: string
}

export interface ClickUpIntegrationStatus {
  is_connected: boolean
  integration: ClickUpIntegration | null
}

class ClickUpApiService {
  /**
   * Obtém a URL de autorização OAuth2 do ClickUp
   */
  async getAuthorizationUrl(returnUrl: string): Promise<string> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/authorization-url`, {
        headers: this.getAuthHeaders(),
        params: { return_url: returnUrl }
      })

      if (response.data.success) {
        return response.data.data.authorization_url
      }

      throw new Error(response.data.message || 'Erro ao obter URL de autorização')
    } catch (error: any) {
      console.error('Error getting ClickUp authorization URL:', error)
      throw error
    }
  }

  /**
   * Obtém o status da integração ClickUp
   */
  async getStatus(): Promise<ClickUpIntegrationStatus> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/status`, {
        headers: this.getAuthHeaders()
      })
      
      return response.data.data
    } catch (error: any) {
      console.error('❌ [ClickUp API] Erro ao obter status:', error)
      console.error('❌ [ClickUp API] Status da resposta:', error.response?.status)
      console.error('❌ [ClickUp API] Dados da resposta:', error.response?.data)
      throw error
    }
  }

  /**
   * Obtém os workspaces do usuário
   */
  async getWorkspaces(): Promise<ClickUpWorkspace[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/workspaces`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Erro ao obter workspaces')
    } catch (error: any) {
      console.error('Error getting ClickUp workspaces:', error)
      throw error
    }
  }

  /**
   * Obtém os spaces de um workspace
   */
  async getWorkspaceSpaces(workspaceId: string): Promise<ClickUpSpace[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/workspaces/${workspaceId}/spaces`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Erro ao obter spaces')
    } catch (error: any) {
      console.error('Error getting ClickUp spaces:', error)
      throw error
    }
  }

  /**
   * Obtém as pastas de um space
   */
  async getSpaceFolders(spaceId: string): Promise<ClickUpFolder[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/spaces/${spaceId}/folders`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Erro ao obter pastas')
    } catch (error: any) {
      console.error('Error getting ClickUp folders:', error)
      throw error
    }
  }

  /**
   * Obtém as listas de uma pasta
   */
  async getFolderLists(folderId: string): Promise<ClickUpList[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/folders/${folderId}/lists`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Erro ao obter listas')
    } catch (error: any) {
      console.error('Error getting ClickUp lists:', error)
      throw error
    }
  }

  /**
   * Obtém as tarefas de uma lista
   */
  async getListTasks(listId: string): Promise<ClickUpTask[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/lists/${listId}/tasks`, {
        headers: this.getAuthHeaders()
      })

      if (response.data.success) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Erro ao obter tarefas')
    } catch (error: any) {
      console.error('Error getting ClickUp tasks:', error)
      throw error
    }
  }

  /**
   * Desconecta a integração ClickUp
   */
  async disconnect(): Promise<void> {
    try {
      await axios.post(`${API_BASE_URL}/api/clickup/disconnect`, {}, {
        headers: this.getAuthHeaders()
      })
    } catch (error: any) {
      console.error('Error disconnecting from ClickUp:', error)
      throw error
    }
  }

  /**
   * Testa a conexão com a API do ClickUp
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/test`, {
        headers: this.getAuthHeaders()
      })
      
      // A rota /test retorna {success: true, message: '...', timestamp: '...'}
      return response.data.success === true
    } catch (error: any) {
      console.error('❌ [ClickUp API] Erro no teste de conexão:', error)
      console.error('❌ [ClickUp API] Status da resposta:', error.response?.status)
      console.error('❌ [ClickUp API] Dados da resposta:', error.response?.data)
      return false
    }
  }

  /**
   * Verifica se as chaves da API estão configuradas
   */
  async hasApiKeys(): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clickup/api-keys`, {
        headers: this.getAuthHeaders()
      })
      
      return response.data.success && response.data.data.has_keys
    } catch (error: any) {
      console.error('❌ [ClickUp API] Erro ao verificar chaves da API:', error)
      return false
    }
  }

  /**
   * Salva as chaves da API
   */
  async saveApiKeys(apiToken: string): Promise<void> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/clickup/api-keys`, {
        api_token: apiToken
      }, {
        headers: this.getAuthHeaders()
      })
    } catch (error: any) {
      console.error('❌ [ClickUp API] Erro ao salvar token da API:', error)
      throw error
    }
  }
  
  private getAuthHeaders() {
    // Usa a mesma chave de autenticação do sistema
    const authKey = 'impersonating-auth-token';
    const authData = localStorage.getItem(authKey);
    
    let token = null;
    if (authData) {
      try {
        const auth = JSON.parse(authData);
        token = auth.api_token;
      } catch (error) {
        console.error('❌ [ClickUp API] Erro ao parsear dados de autenticação:', error);
      }
    }
    
    // Fallback para outras chaves
    if (!token) {
      token = localStorage.getItem('access_token') || 
              localStorage.getItem('token') || 
              localStorage.getItem('auth_token');
    }
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn('⚠️ [ClickUp API] Nenhum token de autenticação encontrado para ClickUp API');
    }
    
    return headers;
  }
}

export const clickupApiService = new ClickUpApiService()
export default clickupApiService
